import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Trophy, Sparkles } from "lucide-react";

const GRADE_COLORS = {
  "Excellent":          "bg-[#E8F0EB] text-[#2D6A4F]",
  "Very Good":          "bg-[#E4EEF7] text-[#28527A]",
  "Good":               "bg-[#F5F0E4] text-[#7A5928]",
  "Average":            "bg-[#FDF3ED] text-[#8A3F1E]",
  "Needs Improvement":  "bg-[#FCECEC] text-[#B94A48]",
};

const RANGE_OPTIONS = [
  { value: 7,  label: "7d"  },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

function Metric({ label, value }) {
  return (
    <div className="rounded-xl bg-white/60 border border-white/70 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="font-display text-lg text-ink mt-0.5 tabular-nums">
        {value ?? "—"}
      </div>
    </div>
  );
}

/**
 * MyPerformanceCard — personal rank card for logged-in doctor. Private,
 * motivational. Shown on the Doctor's dashboard only.
 */
export default function MyPerformanceCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/leaderboard/my-rank?days=${days}`)
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (!loading && (!data || data.is_doctor === false)) return null;

  const found = data?.found;
  const rank = data?.rank;
  const total = data?.total_doctors;
  const score = data?.composite_score;
  const grade = data?.grade;
  const peer = data?.peer_avg_score;
  const top = data?.top_score;
  const gap = score != null && peer != null ? (score - peer).toFixed(1) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-pine to-[#264A3B] text-linen"
      data-testid="my-performance-card"
    >
      {/* Decorative rings */}
      <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-white/5" />
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/5" />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-linen/60">
            <Trophy className="w-3.5 h-3.5" /> My performance
          </div>
          <div className="mt-2 font-display text-2xl">Your rank this window</div>
        </div>
        <div className="inline-flex rounded-full border border-white/20 p-1 text-xs bg-white/5"
             data-testid="myrank-range-toggle">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              data-testid={`myrank-range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`px-2.5 py-1 rounded-full transition ${
                days === r.value ? "bg-linen text-ink" : "text-linen/70 hover:text-linen"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="relative mt-6 text-sm text-linen/70">Loading…</div>
      ) : !found ? (
        <div className="relative mt-6 text-sm text-linen/70">
          No performance data yet for this window. Complete appointments and treatments to see your rank.
        </div>
      ) : (
        <>
          <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-center">
            <div>
              <div className="text-6xl font-display leading-none tabular-nums" data-testid="myrank-position">
                #{rank}
                <span className="text-xl text-linen/50"> / {total}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="font-display text-2xl tabular-nums" data-testid="myrank-score">
                  {score}<span className="text-sm text-linen/50"> /100</span>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${GRADE_COLORS[grade] || GRADE_COLORS.Good}`}>
                  {grade}
                </span>
              </div>
              {gap != null && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-linen/70"
                     data-testid="myrank-vs-peers">
                  <Sparkles className="w-3.5 h-3.5" />
                  {parseFloat(gap) >= 0
                    ? <>You&apos;re <strong>{gap}</strong> pts above peer average</>
                    : <>You&apos;re <strong>{Math.abs(parseFloat(gap))}</strong> pts below peer average</>}
                  {top && rank !== 1 && <span className="ml-2 text-linen/50">• Leader: {top}</span>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Metric label="Appts done"    value={data.metrics?.appts_completed} />
              <Metric label="Patients"      value={data.metrics?.patients_seen} />
              <Metric label="Treatments"    value={data.metrics?.treatments_completed} />
              <Metric label="Completion"    value={data.metrics?.completion_rate != null ? `${Math.round(data.metrics.completion_rate)}%` : "—"} />
              <Metric label="On-time"       value={data.metrics?.on_time_rate != null ? `${Math.round(data.metrics.on_time_rate)}%` : "—"} />
              <Metric label="Window"        value={`${data.window_days}d`} />
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
